import { useHistory, useLocation, useParams } from "react-router-dom"
import { changeIsFloorMap, changeIsTableFinder } from "@store/reducers/paper/actions"
import { getIsFloormap, getIsTableFinder } from "@/store/reducers/paper/selectors"
import { useDispatch, useSelector } from "react-redux"
import { useEffect } from "react"
import { RootState } from "@/store/root-reducer"
import { store } from "@eventdex/core/src/hostApp"
import useLoadTemplate from "@components/Canvas/hooks/useLoadTemplate"



export const currentBaseRoute = () => {
  const state = store.getState() as RootState
  const isFloorMap = getIsFloormap(state)
  const isTableFinder = getIsTableFinder(state)

  if (isFloorMap) {
    return 'floormap'
  }
  
  if (isTableFinder) {
    return 'tablefinder'
  }
  
  return 'badge'
}

export const currentDesignCategory = () => {
  const state = store.getState() as RootState
  const isFloorMap = getIsFloormap(state)
  const isTableFinder = getIsTableFinder(state)

  if (isFloorMap) {
    return 8
  }
  
  if (isTableFinder) {
    return 11
  }
  
  return 5
}

export const AppTypes = {
  floormap: "floormap",
  badge: "badge",
  tablefinder: "tablefinder"
} as const

const RolePermissions = {
  [AppTypes.floormap]: 'Floor Map',
  [AppTypes.tablefinder]: 'Gala',
  [AppTypes.badge]: 'Scan Attendee'
} as const

// Reads the user from the live store rather than a render's closure, so a
// listener registered once (like the edex-permissions one) never sees a stale user.
function hasPermissionForApp(appType: keyof typeof AppTypes): boolean {
  const state = store.getState() as RootState
  const userRole = state.ed_event?.users?.[localStorage?.email]?.role
  if (!userRole) return false

  const requiredPermission = RolePermissions[appType]
  return userRole.split(';').some(role => role.trim() === requiredPermission)
}

export function useDesignRoutes() {
  const history = useHistory()
  const dispatch = useDispatch()
  const { id: designId } = useParams<{ id: string }>()

  function navigateToDesign(id: string) {
    if (designId?.toString() === id?.toString()) return
    history.replace(`/${currentBaseRoute()}/${id}`)
  }

  function switchApp(appType: keyof typeof AppTypes) {
    if (!hasPermissionForApp(appType)) {
      console.warn('User does not have permission for this app type')
      return
    }

    const isFloorMap = appType === AppTypes.floormap
    const isTableFinder = appType === AppTypes.tablefinder

    dispatch(changeIsFloorMap(isFloorMap))
    dispatch(changeIsTableFinder(isTableFinder))
    if (history.location.pathname !== `/${currentBaseRoute()}`) {
      history.replace(`/${currentBaseRoute()}`)
    }
  }

  return { switchApp, navigateToDesign, currentBaseRoute, currentDesignCategory }
}

/**
 * Keeps the open app (badge, floormap, tablefinder) in step with the URL.
 *
 * Call this once, from the Editor. It used to live inside useDesignRoutes,
 * which every template card and panel calls, so one URL change fired
 * switchApp from each of them: dozens of history.replace calls and store
 * writes per navigation, each re-rendering the whole editor and canvas,
 * which froze the page after login.
 */
export function useAppRouteSync() {
  const history = useHistory()
  const location = useLocation()
  const { switchApp, navigateToDesign } = useDesignRoutes()
  const isUserDataLoaded = useSelector(
    (state: RootState) => !!(localStorage?.email && state.ed_event?.users?.[localStorage.email])
  )

  function loadAppFromUrl() {
    if (!isUserDataLoaded) return

    const pathSegments = location.pathname.split('/')
    
    // Skip routing logic if we're on the event route or attendee route
    if (pathSegments.includes('events') || pathSegments.includes('attendee') || pathSegments.includes('gala-preview') || pathSegments.includes('gala-preview-leaflet')) {
      return
    }

    // Check for app type first (tablefinder, floormap, badge)
    const appType = pathSegments.find(segment => 
      Object.values(AppTypes).includes(segment as any)
    ) as keyof typeof AppTypes | undefined

    if (appType) {
      // Check if the current app type is the same as the one in the URL
      const currentAppType = currentBaseRoute()
      if (appType !== currentAppType) {
        if (hasPermissionForApp(appType)) {
          switchApp(appType)
          
          // If there's also a design ID, navigate to it after switching app
          const designIdSegment = pathSegments[pathSegments.length - 1]
          if (designIdSegment && !isNaN(Number(designIdSegment))) {
            navigateToDesign(designIdSegment)
          }
        } else {
          redirectToFirstAvailableApp()
        }
      }
      return
    }

    // If no valid app type found, check if we have a design ID
    const lastSegment = pathSegments[pathSegments.length - 1]
    if (lastSegment && !isNaN(Number(lastSegment))) {
      navigateToDesign(lastSegment)
      return
    }

    // If no valid app type or design ID, redirect to first available app
    redirectToFirstAvailableApp()
  }

  function redirectToFirstAvailableApp() {
    // Define priority order: badge -> floormap -> tablefinder
    const priorityOrder: Array<keyof typeof AppTypes> = ['badge', 'floormap', 'tablefinder']
    
    const firstAvailableApp = priorityOrder.find(appType => hasPermissionForApp(appType))

    if (firstAvailableApp) {
      switchApp(firstAvailableApp)
    } else {
      console.warn('User does not have permission for any app type')
      history.replace(`/${currentBaseRoute()}`)
    }
  }

  useEffect(() => {
    loadAppFromUrl()
  }, [location.pathname, isUserDataLoaded])
}